import { PackageTabs } from '../PackageTabs';
import { UILogistics } from './utils';
import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { BaseInfo } from './BaseInfo';
import { Toolbar } from './Toolbar';
import { History } from 'components/History';
import dayjs from 'dayjs';
import { useTrackingInfo } from 'hooks/business/chat/useTrackingInfo';
import { Empty } from 'antd';
import { MediaViewer } from 'components/MediaViewer';

interface LogisticsInfoProps {
  conversation?: Conversation.Conversation;
  list: UILogistics[];
  sendable: boolean;
  showAll?: boolean;
  cols?: number;
}

const HistoryDescription = ({ updateTime }: { updateTime: string }) => {
  const validUpdateTime = Number(updateTime);
  if (!validUpdateTime) {
    return null;
  }
  const text = dayjs(validUpdateTime * 1000).format('YYYY-MM-DD HH:mm:ss');
  return <div className="text-[#84868C] text-xs mt-[6px]">{text}</div>;
};

export const LogisticsInfo = (props: LogisticsInfoProps) => {
  const { conversation, list, sendable, showAll, cols } = props;
  const { t } = useTranslate();
  const [activeTab, setActiveTab] = useState<number>(0);

  const activeData = useMemo(() => {
    return list[activeTab];
  }, [list, activeTab]);

  const options = useMemo(() => {
    if (list.length > 1) {
      return list.map((_, index) => ({
        value: index,
        label: `${t('包裹')} ${index + 1}`,
      }));
    }
    return [];
  }, [list, t]);

  const {
    viewType,
    trackingInfoItems,
    isLoading: isTriggerTranslateLoading,
    onChangeViewType,
    triggerTranslate,
  } = useTrackingInfo(activeData.trackingInfo);

  const historyItems = useMemo(() => {
    return trackingInfoItems.map((item) => {
      return {
        title: viewType === 'original' ? item.description : item.descriptionTranslation,
        description: (
          <div>
            <HistoryDescription updateTime={item.updateTime} />
            {item.attachment && item.attachment.length > 0 && (
              <div className="mt-2">
                <MediaViewer
                  items={item.attachment.map((att) => ({
                    type: 'image' as const,
                    src: att.url,
                    thumb: att.url,
                  }))}
                  thumbSize={40}
                  gap={8}
                />
              </div>
            )}
          </div>
        ),
      };
    });
  }, [trackingInfoItems, viewType]);

  const isEmpty = historyItems?.length === 0;

  return (
    <div className="flex flex-col gap-3">
      {options.length > 0 ? (
        <PackageTabs
          options={options}
          value={activeTab}
          onChange={(value) => setActiveTab(value as number)}
        />
      ) : null}
      <BaseInfo cols={cols} data={activeData} />
      {isEmpty ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <Toolbar
            conversation={conversation}
            sendable={sendable}
            data={activeData}
            viewType={viewType}
            onToggleViewType={onChangeViewType}
            onTriggerTranslate={triggerTranslate}
          />
          <History items={historyItems} loading={isTriggerTranslateLoading} showAll={showAll} />
        </>
      )}
    </div>
  );
};
